import React, { useState, useEffect } from "react";
import { collection, doc, setDoc, getDoc } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { db } from "../Config/config";
import styled from "styled-components";
import { Calendar, X, Check } from "lucide-react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const TravelRequest = ({ purchase, onClose }) => {
  const [selectedDate, setSelectedDate] = useState(null);
  const [alternativeDate, setAlternativeDate] = useState(null);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [packageDuration, setPackageDuration] = useState(null);

  useEffect(() => {
    const fetchPackageDuration = async () => {
      if (!purchase?.packageId) return;

      try {
        const packageRef = doc(db, "packages", purchase.packageId);
        const packageDoc = await getDoc(packageRef);

        if (packageDoc.exists()) {
          setPackageDuration(packageDoc.data().duration);
        } else {
          console.error("Package not found in database");
        }
      } catch (error) {
        console.error("Error fetching package duration:", error);
      }
    };

    fetchPackageDuration();
  }, [purchase]);

  // Safely access arrays with fallbacks and ensure unique IDs
  const allMembers = [
    ...((purchase?.people || []).map((person, index) => ({
      id: `${person.name}-${person.type}-${index}`, // Added index for true uniqueness
      name: person.name,
      type: person.type,
      dob: person.dob,
    })) || []),
    ...((purchase?.nominees || []).map((nominee, index) => ({
      id: `${nominee.name}-nominee-${index}`, // Added index for true uniqueness
      name: nominee.name,
      type: "nominee",
    })) || []),
  ];

  const locations = purchase?.locations || [];

  const handleMemberSelection = (memberId) => {
    const memberToToggle = allMembers.find((member) => member.id === memberId);
    if (!memberToToggle) return;

    setSelectedMembers((prev) => {
      const isCurrentlySelected = prev.some((member) => member.id === memberId);

      if (isCurrentlySelected) {
        return prev.filter((member) => member.id !== memberId);
      } else {
        return [...prev, memberToToggle];
      }
    });
  };

  const isMemberSelected = (memberId) => {
    return selectedMembers.some((member) => member.id === memberId);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      setError("You must be logged in to submit a request.");
      return;
    }

    // Validation
    if (!selectedDate) {
      setError("Please select a primary date");
      return;
    }
    if (selectedMembers.length === 0) {
      setError("Please select at least one member");
      return;
    }
    if (!selectedLocation) {
      setError("Please select a location");
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      const requestRef = doc(collection(db, "users", user.uid, "requests"));

      const requestData = {
        primaryDate: selectedDate,
        alternativeDate: alternativeDate,
        members: selectedMembers.map((member) => ({
          name: member.name,
          type: member.type,
          ...(member.dob && { dob: member.dob }),
        })),
        location: selectedLocation,
        duration: purchase.duration,
        requestId: requestRef.id,
        purchaseId: purchase.purchaseId,
        packageName: purchase.packageName,
        status: "pending",
        createdAt: new Date(),
        name: user.displayName, 
        userId: user.uid,
      };

      await setDoc(requestRef, requestData);
      setSuccess("Travel request submitted successfully!");

      // Close modal after successful submission
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (error) {
      console.error("Error:", error);
      setError("Error submitting request. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!purchase) {
    return null;
  }

  return (
    <ModalOverlay>
      <ModalContent>
        <CloseButton onClick={onClose}>
          <X size={24} />
        </CloseButton>

        <FormTitle>Travel Request</FormTitle>

        {error && <ErrorMessage>{error}</ErrorMessage>}
        {success && <SuccessMessage>{success}</SuccessMessage>}

        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label>Primary Date*</Label>
            <DatePickerWrapper>
              <DatePicker
                selected={selectedDate}
                onChange={(date) => setSelectedDate(date)}
                minDate={new Date()}
                placeholderText="Select date"
                dateFormat="MMM d, yyyy"
                customInput={<CustomDateInput />}
              />
              <CalendarIcon />
            </DatePickerWrapper>
          </FormGroup>

          <FormGroup>
            <Label>Alternative Date (Optional)</Label>
            <DatePickerWrapper>
              <DatePicker
                selected={alternativeDate}
                onChange={(date) => setAlternativeDate(date)}
                minDate={new Date()}
                placeholderText="Select alternative date"
                dateFormat="MMM d, yyyy"
                customInput={<CustomDateInput />}
              />
              <CalendarIcon />
            </DatePickerWrapper>
          </FormGroup>

          <FormGroup>
            <Label>Select Members* ({selectedMembers.length} selected)</Label>
            <MembersGrid>
              {allMembers.map((member) => (
                <MemberCard
                  key={member.id}
                  $isSelected={isMemberSelected(member.id)}
                  onClick={() => handleMemberSelection(member.id)}
                  role="checkbox"
                  aria-checked={isMemberSelected(member.id)}
                  tabIndex={0}
                >
                  <MemberInfo>
                    <MemberName>{member.name}</MemberName>
                    <MemberType>({member.type})</MemberType>
                  </MemberInfo>
                  <CheckboxWrapper $isSelected={isMemberSelected(member.id)}>
                    <Check size={16} />
                  </CheckboxWrapper>
                </MemberCard>
              ))}
            </MembersGrid>
          </FormGroup>

          <FormGroup>
            <Label>Select Location*</Label>
            <LocationGrid>
              {locations.map((location, index) => (
                <LocationOption
                  key={index}
                  $isSelected={selectedLocation === location}
                  onClick={() => setSelectedLocation(location)}
                >
                  {location}
                </LocationOption>
              ))}
            </LocationGrid>
          </FormGroup>

          <SubmitButton type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Submitting..." : "Submit Request"}
          </SubmitButton>
        </Form>
      </ModalContent>
    </ModalOverlay>
  );
};

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: white;
  padding: 30px;
  border-radius: 10px;
  width: 100%;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: none;
  border: none;
  cursor: pointer;
  color: #441b77;
  padding: 5px;

  &:hover {
    opacity: 0.8;
  }
`;

const FormTitle = styled.h2`
  color: #441b77;
  font-family: "Poppins-SemiBold", sans-serif;
  margin-bottom: 30px;
  text-align: center;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 25px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Label = styled.label`
  color: #441b77;
  font-family: "Poppins-Medium", sans-serif;
  font-size: 14px;
`;

const DatePickerWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const CustomDateInput = styled.input`
  width: 100%;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 5px;
  font-family: "Poppins-Regular", sans-serif;
  padding-right: 40px;

  &:focus {
    outline: none;
    border-color: #441b77;
  }
`;

const CalendarIcon = styled(Calendar)`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #441b77;
`;

const MembersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 15px;
`;

const MemberCard = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  border: 2px solid ${(props) => (props.$isSelected ? "#441B77" : "#ddd")};
  border-radius: 5px;
  cursor: pointer;
  transition: all 0.3s ease;
  background: ${(props) => (props.$isSelected ? "#fff5f7" : "white")};

  &:hover {
    border-color: #441b77;
    background: ${(props) => (props.$isSelected ? "#fff5f7" : "#fff9fa")};
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px #441b77;
  }
`;

const MemberInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const MemberName = styled.span`
  color: #441b77;
  font-family: "Poppins-Medium", sans-serif;
  font-size: 14px;
`;

const MemberType = styled.small`
  color: #666;
  font-size: 12px;
  font-family: "Poppins-Regular", sans-serif;
`;

const CheckboxWrapper = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid ${(props) => (props.$isSelected ? "#441B77" : "#ddd")};
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(props) => (props.$isSelected ? "#441B77" : "white")};
  transition: all 0.3s ease;

  svg {
    color: white;
    opacity: ${(props) => (props.$isSelected ? 1 : 0)};
    transition: opacity 0.3s ease;
  }
`;

const LocationGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 15px;
`;

const LocationOption = styled.div`
  padding: 12px;
  border: 2px solid ${(props) => (props.$isSelected ? "#441B77" : "#ddd")};
  border-radius: 5px;
  cursor: pointer;
  transition: all 0.3s ease;
  text-align: center;
  background: ${(props) => (props.$isSelected ? "#fff5f7" : "white")};
  color: #441b77;
  font-family: "Poppins-Medium", sans-serif;

  &:hover {
    border-color: #441b77;
  }
`;

const SubmitButton = styled.button`
  background-color: #441b77;
  color: white;
  border: none;
  padding: 15px;
  border-radius: 5px;
  font-family: "Poppins-SemiBold", sans-serif;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 20px;

  &:hover {
    background-color: #641029;
  }

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  color: #ff4444;
  background-color: #ffe6e6;
  padding: 12px;
  border-radius: 5px;
  margin-bottom: 20px;
  font-family: "Poppins-Medium", sans-serif;
`;

const SuccessMessage = styled.div`
  color: #4caf50;
  background-color: #ebf7eb;
  padding: 12px;
  border-radius: 5px;
  margin-bottom: 20px;
  font-family: "Poppins-Medium", sans-serif;
`;

export default TravelRequest;
